import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { messageAPI, staffAPI } from '../../lib/api';
import { 
  ChatCircle, 
  PaperPlaneTilt, 
  EnvelopeSimple,
  User,
  Check,
  CheckCircle
} from '@phosphor-icons/react';

export default function MessagesPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    receiver_id: '',
    subject: '',
    content: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [messagesRes, staffRes] = await Promise.all([
        messageAPI.getAll(),
        staffAPI.getAll().catch(() => ({ data: { staff: [] } })),
      ]);
      setMessages(messagesRes.data);
      // Filter out current user
      const staffList = staffRes.data.staff || staffRes.data || [];
      setUsers(staffList.filter(u => u.id !== user?.id));
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.receiver_id || !formData.subject || !formData.content) return;
    
    setSending(true);
    try {
      await messageAPI.send(formData);
      setShowCompose(false);
      setFormData({ receiver_id: '', subject: '', content: '' });
      loadData();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleRead = async (message) => {
    setSelectedMessage(message);
    if (!message.read && message.receiver_id === user?.id) {
      await messageAPI.markRead(message.id);
      loadData();
    }
  };

  const inbox = messages.filter(m => m.receiver_id === user?.id);
  const sent = messages.filter(m => m.sender_id === user?.id);
  const unreadCount = inbox.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Messages
          </h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'Secure messaging with your care team'}
          </p>
        </div>
        <button
          onClick={() => setShowCompose(true)}
          className="btn-teal flex items-center gap-2"
          data-testid="compose-message-btn"
        >
          <PaperPlaneTilt size={18} weight="bold" />
          New Message
        </button>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full" data-testid="compose-modal">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">New Message</h2>
            </div>
            
            <form onSubmit={handleSend} className="p-6 space-y-4">
              <div>
                <label className="label-clinical">To</label>
                <select
                  value={formData.receiver_id}
                  onChange={(e) => setFormData({ ...formData, receiver_id: e.target.value })}
                  className="input-clinical"
                  data-testid="message-recipient"
                >
                  <option value="">Select recipient</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-clinical">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input-clinical"
                  placeholder="Message subject"
                  data-testid="message-subject"
                />
              </div>

              <div>
                <label className="label-clinical">Message</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-clinical min-h-[150px]"
                  placeholder="Type your message..."
                  data-testid="message-content"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCompose(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || !formData.receiver_id || !formData.subject || !formData.content}
                  className="flex-1 btn-teal"
                  data-testid="send-message-btn"
                >
                  {sending ? <div className="spinner mx-auto" /> : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">{selectedMessage.subject}</h2>
                <button onClick={() => setSelectedMessage(null)} className="text-slate-400 hover:text-slate-600">
                  &times;
                </button>
              </div>
              <div className="text-sm text-slate-500 mt-1">
                From: {selectedMessage.sender_name} | {new Date(selectedMessage.created_at).toLocaleString()}
              </div>
            </div>
            <div className="p-6">
              <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.content}</p>
            </div>
            <div className="p-6 border-t border-slate-200">
              <button onClick={() => setSelectedMessage(null)} className="btn-outline w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inbox */}
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <EnvelopeSimple size={18} /> Inbox
              </h2>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>
          
          {inbox.length === 0 ? (
            <div className="p-12 text-center">
              <ChatCircle size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No messages yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {inbox.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleRead(msg)}
                  className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${!msg.read ? 'bg-teal-50/50' : ''}`}
                  data-testid={`inbox-msg-${msg.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!msg.read ? 'bg-teal-100' : 'bg-slate-100'}`}>
                      <User size={16} className={!msg.read ? 'text-teal-600' : 'text-slate-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm ${!msg.read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {msg.sender_name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className={`text-sm truncate ${!msg.read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                        {msg.subject}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {msg.content}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sent */}
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <CheckCircle size={18} /> Sent
            </h2>
          </div>
          
          {sent.length === 0 ? (
            <div className="p-12 text-center">
              <PaperPlaneTilt size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No sent messages</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {sent.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleRead(msg)}
                  className="w-full p-4 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`sent-msg-${msg.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                      <Check size={16} className="text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">To: {users.find(u => u.id === msg.receiver_id)?.first_name || 'User'}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-sm text-slate-700 truncate">{msg.subject}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
